import { Archive, ArrowRight, BookOpen, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, FileText, Filter, Globe, Layers3, Link2, Lock, Pencil, Plus, Save, Share2, Tag, X, Youtube } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { BuiltInSourceKind, EntityLink, SourceKind, SourceNode } from "../types";
import { EntityLinksPanel } from "./EntityLinksPanel";
import { StatusBadge } from "./StatusBadge";
import { defaultStatusForKind, statusColor, statusMeta, statusOptionsForKind } from "../lib/lifecycle";
import { TagPicker } from "./TagPicker";
import { useSecondClickConfirm } from "../lib/useSecondClickConfirm";

type Props = {
  nodes: SourceNode[];
  selectedId: string;
  onSelect: (id: string) => void;
  onCreateSource: (input: { title: string; kind: SourceKind; parentId?: string; status?: string; tags?: string[]; properties?: Record<string, unknown> }) => Promise<void | string | undefined>;
  onUpdateSource: (id: string, input: { title?: string; kind?: SourceKind; progress?: number; parentId?: string | null; status?: string }) => Promise<void>;
  onArchiveSource: (id: string) => Promise<void>;
  onLoadLinks: (objectId: string) => Promise<void>;
  onCreateLink: (objectId: string, input: { url: string; label?: string }) => Promise<void>;
  onDeleteLink: (objectId: string, linkId: string) => Promise<void>;
  onOpenWorkspace?: (source: { id: string; kind: SourceKind; title: string; properties?: Record<string, unknown> }) => void;
  onSetVisibility?: (objectId: string, visibility: "public" | "private") => Promise<void>;
  linksByObject: Record<string, EntityLink[]>;
  sourceError?: string;
  linkError?: string;
};

const iconByKind = {
  article: FileText,
  book: BookOpen,
  course: Layers3,
  lesson: FileText,
  module: Layers3,
  topic: FileText,
  youtube: Youtube,
  output: Share2
} satisfies Record<BuiltInSourceKind, typeof FileText>;

const sourceKindOptions: SourceKind[] = ["course", "module", "lesson", "book", "article", "topic", "youtube", "output"];
const sourceFilterOptions = [
  { id: "all", label: "All" },
  { id: "book", label: "Book" },
  { id: "link", label: "Link" },
  { id: "course", label: "Course" }
] as const;

type SourceFilter = (typeof sourceFilterOptions)[number]["id"];
type CreateMode = "link" | "book" | "course" | "topic" | "output" | "quick";

const createModes: Array<{ id: CreateMode; label: string; description: string; kind: SourceKind }> = [
  { id: "link", label: "Link", description: "Article, YouTube, Substack, blog, tài liệu web", kind: "article" },
  { id: "book", label: "Book", description: "Sách hoặc cây mục lục dài", kind: "book" },
  { id: "course", label: "Course", description: "Khóa học, module, lesson", kind: "course" },
  { id: "topic", label: "Topic", description: "Chủ đề gom note và source liên quan", kind: "topic" },
  { id: "output", label: "Output", description: "Bài/ấn phẩm bạn đã đăng ở nơi khác", kind: "output" },
  { id: "quick", label: "Quick", description: "Tạo nhanh một source chỉ với tên", kind: "course" }
];

function buildChildrenMap(nodes: SourceNode[]) {
  const map = new Map<string, SourceNode[]>();
  for (const node of nodes) {
    if (!node.parentId) {
      continue;
    }
    map.set(node.parentId, [...(map.get(node.parentId) ?? []), node]);
  }
  return map;
}

type SourceSortStats = {
  noteTotal: number;
  itemTotal: number;
  itemWithNotes: number;
  noteActivity: number;
  sourceActivity: number;
};

function timestampValue(value?: string | null) {
  const parsed = Date.parse(value ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
}

function hostname(value: string) {
  try {
    return new URL(value).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function inferLinkMetadata(value: string) {
  try {
    const url = new URL(value.trim());
    const host = url.hostname.toLowerCase().replace(/^www\./, "").replace(/^m\./, "");
    const parts = url.pathname.split("/").filter(Boolean);

    if (host === "youtu.be" || host.endsWith("youtube.com")) {
      const author =
        parts[0]?.startsWith("@")
          ? parts[0]
          : ["channel", "c", "user"].includes(parts[0] ?? "")
            ? parts[1] ?? ""
            : "";
      return { kind: "youtube" as SourceKind, platform: "YouTube", author };
    }

    if (host.endsWith("substack.com")) {
      const subdomain = host.replace(".substack.com", "");
      const author = host === "substack.com" && parts[0]?.startsWith("@") ? parts[0] : subdomain;
      return { kind: "article" as SourceKind, platform: "Substack", author: author === "substack" ? "" : author };
    }

    if (host.includes("facebook.com") || host === "fb.watch") {
      return { kind: "article" as SourceKind, platform: "Facebook", author: parts[0] ?? "" };
    }

    if (host.includes("medium.com")) {
      return { kind: "article" as SourceKind, platform: "Medium", author: parts.find((part) => part.startsWith("@"))?.replace(/^@/, "") ?? "" };
    }

    if (host.includes("x.com") || host.includes("twitter.com")) {
      return { kind: "article" as SourceKind, platform: host.includes("twitter.com") ? "Twitter" : "X", author: parts[0] ?? "" };
    }

    return { kind: "article" as SourceKind, platform: hostname(value), author: "" };
  } catch {
    return { kind: "article" as SourceKind, platform: "", author: "" };
  }
}

function collectSourceStats(node: SourceNode, childrenByParent: Map<string, SourceNode[]>, statsById: Map<string, SourceSortStats>): SourceSortStats {
  const cached = statsById.get(node.id);

  if (cached) {
    return cached;
  }

  const stats = (childrenByParent.get(node.id) ?? []).reduce<SourceSortStats>(
    (current, child) => {
      const childStats = collectSourceStats(child, childrenByParent, statsById);
      return {
        noteTotal: current.noteTotal + childStats.noteTotal,
        itemTotal: current.itemTotal + childStats.itemTotal,
        itemWithNotes: current.itemWithNotes + childStats.itemWithNotes,
        noteActivity: Math.max(current.noteActivity, childStats.noteActivity),
        sourceActivity: Math.max(current.sourceActivity, childStats.sourceActivity)
      };
    },
    {
      noteTotal: node.noteCount,
      itemTotal: 1,
      itemWithNotes: node.noteCount > 0 ? 1 : 0,
      noteActivity: timestampValue(node.noteActivityAt),
      sourceActivity: timestampValue(node.sourceUpdatedAt)
    }
  );

  statsById.set(node.id, stats);
  return stats;
}

function compareSourcesByActivity(left: SourceNode, right: SourceNode, statsById: Map<string, SourceSortStats>) {
  const leftStats = statsById.get(left.id) ?? { noteTotal: left.noteCount, itemTotal: 1, itemWithNotes: left.noteCount > 0 ? 1 : 0, noteActivity: 0, sourceActivity: 0 };
  const rightStats = statsById.get(right.id) ?? { noteTotal: right.noteCount, itemTotal: 1, itemWithNotes: right.noteCount > 0 ? 1 : 0, noteActivity: 0, sourceActivity: 0 };
  const leftActivity = Math.max(leftStats.noteActivity, leftStats.sourceActivity);
  const rightActivity = Math.max(rightStats.noteActivity, rightStats.sourceActivity);

  return (
    rightActivity - leftActivity ||
    rightStats.sourceActivity - leftStats.sourceActivity ||
    rightStats.noteActivity - leftStats.noteActivity ||
    left.title.localeCompare(right.title)
  );
}

function sourceFillPercent(node: SourceNode, stats?: SourceSortStats) {
  if (node.progress > 0) {
    return node.progress;
  }

  if (!stats || stats.itemTotal <= 0 || stats.itemWithNotes <= 0) {
    return 0;
  }

  return Math.min(100, Math.max(12, Math.round((stats.itemWithNotes / stats.itemTotal) * 100)));
}

function sourceMatchesKindFilter(node: SourceNode, filter: SourceFilter) {
  if (filter === "all") {
    return true;
  }

  if (filter === "book") {
    return node.kind === "book";
  }

  if (filter === "link") {
    return node.kind === "article" || node.kind === "youtube" || node.kind === "output" || typeof node.properties?.sourceUrl === "string";
  }

  return ["course", "module", "lesson", "topic"].includes(node.kind);
}

function sourceMatchesStatusFilter(node: SourceNode, statusFilter: string) {
  if (statusFilter === "all") {
    return true;
  }
  return (node.status ?? defaultStatusForKind(node.kind)) === statusFilter;
}

function sourceMatchesTagFilter(node: SourceNode, tagFilter: string[]) {
  if (tagFilter.length === 0) {
    return true;
  }
  const nodeTags = new Set(node.tags ?? []);
  return tagFilter.some((tag) => nodeTags.has(tag));
}

function sourceMatchesFilter(node: SourceNode, filter: SourceFilter, statusFilter: string, tagFilter: string[]) {
  return sourceMatchesKindFilter(node, filter) && sourceMatchesStatusFilter(node, statusFilter) && sourceMatchesTagFilter(node, tagFilter);
}

function sourceAncestorIds(node: SourceNode | undefined, nodeById: Map<string, SourceNode>) {
  const ids: string[] = [];
  let current = node?.parentId ? nodeById.get(node.parentId) : undefined;
  const seen = new Set<string>();
  while (current && !seen.has(current.id)) {
    ids.unshift(current.id);
    seen.add(current.id);
    current = current.parentId ? nodeById.get(current.parentId) : undefined;
  }
  return ids;
}

function isWorkspaceLinkKind(kind: SourceKind, properties?: Record<string, unknown>) {
  return kind === "article" || kind === "youtube" || kind === "output" || typeof properties?.sourceUrl === "string";
}

function subtreeMatchesFilter(
  node: SourceNode,
  filter: SourceFilter,
  statusFilter: string,
  tagFilter: string[],
  childrenByParent: Map<string, SourceNode[]>,
  cache: Map<string, boolean>
): boolean {
  const cached = cache.get(node.id);

  if (cached !== undefined) {
    return cached;
  }

  const matched: boolean =
    sourceMatchesFilter(node, filter, statusFilter, tagFilter) ||
    (childrenByParent.get(node.id) ?? []).some((child) =>
      subtreeMatchesFilter(child, filter, statusFilter, tagFilter, childrenByParent, cache)
    );

  cache.set(node.id, matched);
  return matched;
}

export function SourceTree({
  nodes,
  selectedId,
  onSelect,
  onCreateSource,
  onUpdateSource,
  onArchiveSource,
  onLoadLinks,
  onCreateLink,
  onDeleteLink,
  onOpenWorkspace,
  onSetVisibility,
  linksByObject,
  sourceError,
  linkError
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [createMode, setCreateMode] = useState<CreateMode>("link");
  const [createdSource, setCreatedSource] = useState<{ id: string; kind: SourceKind; title: string; properties?: Record<string, unknown> } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<SourceKind>("course");
  const [sourceUrl, setSourceUrl] = useState("");
  const [platform, setPlatform] = useState("");
  const [author, setAuthor] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [parentId, setParentId] = useState("");
  const [createTags, setCreateTags] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editKind, setEditKind] = useState<SourceKind>("course");
  const [editProgress, setEditProgress] = useState(0);
  const [editStatus, setEditStatus] = useState("not_started");
  const [isEditing, setIsEditing] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const [linkEditorId, setLinkEditorId] = useState("");
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const archiveConfirm = useSecondClickConfirm();

  // All tags in use across sources — the available chips for the tag filter + create suggestions.
  const availableTags = useMemo(() => {
    const set = new Set<string>();
    nodes.forEach((node) => (node.tags ?? []).forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [nodes]);

  // When picking a parent in the create form, pre-fill tags with a copy of the parent's tags
  // (copy-on-create inheritance) so adding here appends rather than replaces.
  useEffect(() => {
    setCreateTags(parentId ? nodes.find((node) => node.id === parentId)?.tags ?? [] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  // Source statuses available to filter on: the seeded "source" lifecycle plus any custom
  // status already used by a source node (so newly-added statuses are filterable too).
  const statusFilterOptions = useMemo(() => {
    const base = statusOptionsForKind("book");
    const baseSlugs = new Set(base.map((option) => option.slug));
    const custom = Array.from(new Set(nodes.map((node) => node.status).filter((slug): slug is string => Boolean(slug)))).filter(
      (slug) => !baseSlugs.has(slug)
    );
    return [...base, ...custom.map((slug) => statusMeta("book", slug))];
  }, [nodes]);

  const childrenByParent = useMemo(() => buildChildrenMap(nodes), [nodes]);
  const nodeById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const selectedNode = useMemo(() => nodeById.get(selectedId), [nodeById, selectedId]);
  const sourceStatsById = useMemo(() => {
    const statsById = new Map<string, SourceSortStats>();
    for (const node of nodes) {
      collectSourceStats(node, childrenByParent, statsById);
    }
    return statsById;
  }, [childrenByParent, nodes]);
  const filterMatchById = useMemo(() => {
    const cache = new Map<string, boolean>();
    for (const node of nodes) {
      subtreeMatchesFilter(node, sourceFilter, statusFilter, tagFilter, childrenByParent, cache);
    }
    return cache;
  }, [childrenByParent, nodes, sourceFilter, statusFilter, tagFilter]);
  const parents = useMemo(
    () =>
      nodes
        .filter((node) => !node.parentId && (filterMatchById.get(node.id) ?? false))
        .map((node) => ({ node, totalNotes: sourceStatsById.get(node.id)?.noteTotal ?? node.noteCount }))
        .sort((left, right) => compareSourcesByActivity(left.node, right.node, sourceStatsById)),
    [filterMatchById, nodes, sourceStatsById]
  );

  useEffect(() => {
    const normalizedUrl = sourceUrl.trim();
    if (createMode !== "link" && createMode !== "output") {
      return;
    }

    if (!normalizedUrl || !/^https?:\/\//i.test(normalizedUrl)) {
      return;
    }

    const inferred = inferLinkMetadata(normalizedUrl);
    setPlatform((current) => current || inferred.platform);
    setAuthor((current) => current || inferred.author);
    setLinkLabel((current) => current || inferred.platform || hostname(normalizedUrl));
    setKind((current) => (current === "course" || current === "article" ? inferred.kind : current));
  }, [createMode, sourceUrl]);
  useEffect(() => {
    if (!selectedNode) {
      return;
    }

    setEditTitle(selectedNode.title);
    setEditKind(selectedNode.kind);
    setEditProgress(selectedNode.progress);
    setEditStatus(selectedNode.status ?? defaultStatusForKind(selectedNode.kind));

    const ancestorIds = sourceAncestorIds(selectedNode, nodeById);
    if (ancestorIds.length > 0) {
      setExpandedIds((current) => Array.from(new Set([...current, ...ancestorIds])));
    }
  }, [nodeById, selectedNode]);

  const openCreate = (mode: CreateMode = "link") => {
    const modeConfig = createModes.find((item) => item.id === mode) ?? createModes[0];
    setCreateMode(mode);
    setKind(modeConfig.kind);
    setTitle("");
    setSourceUrl("");
    setPlatform("");
    setAuthor("");
    setLinkLabel("");
    setParentId("");
    setCreatedSource(null);
    setIsOpen(true);
  };

  const closeCreate = () => {
    setIsOpen(false);
    setCreatedSource(null);
  };

  const handleCreateMode = (mode: CreateMode) => {
    const modeConfig = createModes.find((item) => item.id === mode) ?? createModes[0];
    setCreateMode(mode);
    setKind((current) => (mode === "link" && (current === "article" || current === "youtube") ? current : modeConfig.kind));
    if (mode === "output") {
      setKind("output");
    }
    if (mode !== "link" && mode !== "output") {
      setSourceUrl("");
      setPlatform("");
      setAuthor("");
      setLinkLabel("");
    }
    setCreatedSource(null);
  };

  const handleCreate = async () => {
    const normalizedUrl = sourceUrl.trim();
    const normalized = title.trim() || hostname(normalizedUrl) || "Untitled source";
    const normalizedPlatform = platform.trim();
    const normalizedAuthor = author.trim();
    const shouldSaveLink = (createMode === "link" || createMode === "output") && normalizedUrl;

    if (!title.trim() && !shouldSaveLink) {
      return;
    }

    setIsSaving(true);

    try {
      const properties =
        normalizedUrl || normalizedPlatform || normalizedAuthor
          ? {
              ...(normalizedUrl ? { sourceUrl: normalizedUrl } : {}),
              ...(normalizedPlatform ? { platform: normalizedPlatform } : {}),
              ...(normalizedAuthor ? { author: normalizedAuthor } : {})
            }
          : undefined;
      const createdId = await onCreateSource({
        title: normalized,
        kind: createMode === "output" ? "output" : kind,
        parentId: parentId || undefined,
        tags: createTags.length ? createTags : undefined,
        properties
      });
      if (createdId && shouldSaveLink) {
        await onCreateLink(createdId, {
          url: normalizedUrl,
          label: linkLabel.trim() || normalizedPlatform || hostname(normalizedUrl)
        });
        await onLoadLinks(createdId);
      }
      if (createdId) {
        setCreatedSource({
          id: createdId,
          kind: createMode === "output" ? "output" : kind,
          title: normalized,
          properties
        });
      }
      setTitle("");
      setSourceUrl("");
      setPlatform("");
      setAuthor("");
      setLinkLabel("");
      setParentId("");
      setCreateTags([]);
      if (!createdId) {
        setIsOpen(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedNode || !editTitle.trim()) {
      return;
    }

    setIsEditing(true);

    try {
      await onUpdateSource(selectedNode.id, {
        title: editTitle.trim(),
        kind: editKind,
        progress: editProgress,
        status: editStatus
      });
      setEditOpen(false);
    } finally {
      setIsEditing(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedNode) {
      return;
    }

    setIsEditing(true);

    try {
      await onArchiveSource(selectedNode.id);
      setEditOpen(false);
    } finally {
      setIsEditing(false);
    }
  };

  const handleToggleLinks = async () => {
    if (!selectedNode) {
      return;
    }

    const nextOpen = !linksOpen;
    setLinksOpen(nextOpen);

    if (nextOpen && !linksByObject[selectedNode.id]) {
      await onLoadLinks(selectedNode.id);
    }
  };

  // EPIC-0: if a source has a reference link, turn its kind badge ("Article", "Book"…)
  // into the clickable button that opens that link in a new tab. Adding/managing links
  // and the visibility toggle live in the pencil edit panel to keep rows tidy.
  const renderKindBadge = (node: SourceNode, badge: ReactNode) => {
    const link = (linksByObject[node.id] ?? node.links ?? [])[0];
    if (!link) {
      return badge;
    }
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noreferrer"
        title={`Mở link: ${link.url}`}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        className="inline-flex shrink-0 items-center gap-1 rounded-md hover:opacity-80"
      >
        {badge}
        <ExternalLink size={11} className="text-brand" aria-hidden="true" />
      </a>
    );
  };

  // EPIC-0: per-row "edit mode" controls (shown only when the pencil is on). Each source
  // block reveals its own visibility toggle + add-link, so you set them per source inline.
  const toggleLinkEditor = async (id: string) => {
    const next = linkEditorId === id ? "" : id;
    setLinkEditorId(next);
    if (next && !linksByObject[id]) {
      await onLoadLinks(id);
    }
  };

  const renderRowEditStrip = (node: SourceNode) => {
    if (!onSetVisibility) {
      return null;
    }
    const isPublic = node.visibility === "public";
    return (
      <div className="mt-2 flex flex-wrap items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          title="Riêng tư: chỉ mình bạn thấy."
          onClick={(event) => {
            event.stopPropagation();
            void onSetVisibility(node.id, "private");
          }}
          className={`inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px] font-bold ${
            !isPublic ? "border-slate-300 bg-slate-100 text-slate-800" : "border-line bg-white text-slate-500 hover:bg-slate-50"
          }`}
        >
          <Lock size={12} aria-hidden="true" />
          Riêng tư
        </button>
        <button
          type="button"
          title="Công khai: xuất hiện ở trang public."
          onClick={(event) => {
            event.stopPropagation();
            void onSetVisibility(node.id, "public");
          }}
          className={`inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px] font-bold ${
            isPublic ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-line bg-white text-slate-500 hover:bg-slate-50"
          }`}
        >
          <Globe size={12} aria-hidden="true" />
          Công khai
        </button>
        <button
          type="button"
          title="Thêm / quản lý link tham khảo"
          onClick={(event) => {
            event.stopPropagation();
            void toggleLinkEditor(node.id);
          }}
          className="inline-flex h-7 items-center gap-1 rounded-md border border-dashed border-line bg-white px-2 text-[11px] font-bold text-muted hover:bg-slate-50"
        >
          <Plus size={12} aria-hidden="true" />
          link
        </button>
      </div>
    );
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const handleNodeSelect = (node: SourceNode, hasChildren: boolean) => {
    if (hasChildren) {
      toggleExpanded(node.id);
    }
    onSelect(node.id);
  };

  const sortedChildren = (nodeId: string) =>
    [...(childrenByParent.get(nodeId) ?? [])]
      .filter((child) => filterMatchById.get(child.id) ?? false)
      .sort((left, right) => compareSourcesByActivity(left, right, sourceStatsById));

  const renderChildNode = (node: SourceNode) => {
    const children = sortedChildren(node.id);
    const expanded = expandedIds.includes(node.id);
    return (
      <div key={node.id} className="space-y-1">
        <div
          role="button"
          tabIndex={0}
          className={`flex h-9 w-full cursor-pointer items-center gap-2 rounded-md border px-2 text-left text-xs transition ${
            selectedId === node.id
              ? "border-blue-200 bg-blue-50 text-brand"
              : "border-transparent bg-white text-slate-700 hover:border-line hover:bg-slate-50"
          }`}
          onClick={() => handleNodeSelect(node, children.length > 0)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleNodeSelect(node, children.length > 0);
            }
          }}
        >
          {children.length > 0 ? (
            expanded ? (
              <ChevronDown size={14} className="shrink-0 text-muted" aria-hidden="true" />
            ) : (
              <ChevronRight size={14} className="shrink-0 text-muted" aria-hidden="true" />
            )
          ) : (
            <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          )}
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: statusColor(node.kind, node.status) }}
            title={statusMeta(node.kind, node.status).label}
          />
          <span className="min-w-0 flex-1 truncate font-semibold">{node.title}</span>
        </div>
        {editOpen ? <div className="pl-5">{renderRowEditStrip(node)}</div> : null}
        {editOpen && linkEditorId === node.id ? (
          <EntityLinksPanel
            objectId={node.id}
            links={linksByObject[node.id] ?? node.links ?? []}
            error={linkError}
            onLoad={onLoadLinks}
            onCreate={onCreateLink}
            onDelete={onDeleteLink}
          />
        ) : null}
        {expanded && children.length > 0 ? <div className="space-y-1 pl-4">{children.map((child) => renderChildNode(child))}</div> : null}
      </div>
    );
  };

  return (
    <>
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-line bg-white shadow-panel">
      <div className="border-b border-line px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold">Learning sources</h2>
            <p className="text-xs font-medium text-muted">Cập nhật mới nhất trước</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-line text-slate-600 hover:bg-slate-50"
              type="button"
              aria-label="Create source"
              onClick={() => openCreate("link")}
            >
              <Plus size={17} aria-hidden="true" />
            </button>
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-line text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
              type="button"
              aria-label="Edit selected source"
              disabled={!selectedNode}
              onClick={() => setEditOpen((value) => !value)}
            >
              <Pencil size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className="border-b border-line bg-white px-4 py-2">
        <div className="flex flex-wrap gap-2">
          {sourceFilterOptions.map((option) => (
            <button
              key={option.id}
              className={`inline-flex h-8 items-center justify-center rounded-md border px-3 text-xs font-bold transition-colors ${
                sourceFilter === option.id
                  ? "border-blue-200 bg-blue-50 text-brand"
                  : "border-line bg-white text-slate-600 hover:bg-slate-50"
              }`}
              type="button"
              onClick={() => setSourceFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Filter size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
          <div className="relative min-w-0 flex-1">
            <select
              className={`h-8 w-full appearance-none rounded-md border px-2 pr-7 text-xs font-bold transition-colors ${
                statusFilter === "all" ? "border-line bg-white text-slate-600" : "border-blue-200 bg-blue-50 text-brand"
              }`}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              aria-label="Lọc theo trạng thái"
            >
              <option value="all">Mọi trạng thái</option>
              {statusFilterOptions.map((option) => (
                <option key={option.slug} value={option.slug}>
                  {option.labelVi}
                </option>
              ))}
            </select>
            <ChevronDown size={13} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          </div>
          {statusFilter !== "all" ? (
            <button
              type="button"
              onClick={() => setStatusFilter("all")}
              className="inline-flex h-8 shrink-0 items-center rounded-md border border-line bg-white px-2 text-xs font-bold text-slate-500 hover:bg-slate-50"
              title="Xoá lọc trạng thái"
            >
              <X size={13} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {availableTags.length > 0 ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Tag size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
            {availableTags.map((tag) => {
              const active = tagFilter.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() =>
                    setTagFilter((current) => (current.includes(tag) ? current.filter((value) => value !== tag) : [...current, tag]))
                  }
                  className={`inline-flex h-7 items-center rounded-md border px-2 text-xs font-bold transition-colors ${
                    active ? "border-cyan-300 bg-cyan-50 text-cyan-700" : "border-line bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {tag}
                </button>
              );
            })}
            {tagFilter.length > 0 ? (
              <button
                type="button"
                onClick={() => setTagFilter([])}
                className="inline-flex h-7 shrink-0 items-center rounded-md border border-line bg-white px-2 text-xs font-bold text-slate-500 hover:bg-slate-50"
                title="Xoá lọc tag"
              >
                <X size={13} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : null}
        {selectedNode && onOpenWorkspace ? (
          <button
            className="mt-2 inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
            type="button"
            onClick={() => onOpenWorkspace(selectedNode)}
          >
            <ArrowRight size={14} aria-hidden="true" />
            {isWorkspaceLinkKind(selectedNode.kind, selectedNode.properties) ? "Mở Link workspace" : "Mở Book workspace"}
          </button>
        ) : null}
      </div>

      {false ? (
        <div className="border-b border-line bg-slate-50 p-3">
          <div className="space-y-2">
            <input
              className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Ten source hoac tieu de link"
              aria-label="Source title"
            />
            <input
              className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
              value={sourceUrl}
              onChange={(event) => setSourceUrl(event.target.value)}
              placeholder="https://... (article, YouTube, Substack, blog, tai lieu)"
              aria-label="Source URL"
            />
            <div className="grid grid-cols-2 gap-2">
              <select
                className="h-10 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-slate-700"
                value={kind}
                onChange={(event) => setKind(event.target.value as SourceKind)}
                aria-label="Source kind"
              >
                {sourceKindOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <select
                className="h-10 min-w-0 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-slate-700"
                value={parentId}
                onChange={(event) => setParentId(event.target.value)}
                aria-label="Parent source"
              >
                <option value="">Không có parent</option>
                {nodes.map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input
                className="h-10 min-w-0 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
                value={platform}
                onChange={(event) => setPlatform(event.target.value)}
                placeholder="Platform"
                aria-label="Source platform"
              />
              <input
                className="h-10 min-w-0 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
                value={author}
                onChange={(event) => setAuthor(event.target.value)}
                placeholder="Tac gia/kenh"
                aria-label="Source author"
              />
              <input
                className="h-10 min-w-0 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
                value={linkLabel}
                onChange={(event) => setLinkLabel(event.target.value)}
                placeholder="Ten link"
                aria-label="Link label"
              />
            </div>
            {sourceUrl.trim() && !/^https?:\/\//i.test(sourceUrl.trim()) ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                Link can bat dau bang http:// hoac https:// de luu URL goc.
              </p>
            ) : null}
            <button
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              type="button"
              disabled={(!title.trim() && !sourceUrl.trim()) || Boolean(sourceUrl.trim() && !/^https?:\/\//i.test(sourceUrl.trim())) || isSaving}
              onClick={handleCreate}
            >
              <Plus size={16} aria-hidden="true" />
              {isSaving ? "Đang lưu" : "Tạo nguồn học"}
            </button>
            {sourceError ? (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                {sourceError}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {editOpen && selectedNode ? (
        <div className="border-b border-line bg-blue-50 p-3">
          <div className="space-y-2">
            <input
              className="h-10 w-full rounded-lg border border-blue-200 bg-white px-3 text-sm font-semibold text-ink"
              value={editTitle}
              onChange={(event) => setEditTitle(event.target.value)}
              aria-label="Edit source title"
            />
            <div className="grid grid-cols-2 gap-2">
              <select
                className="h-10 rounded-lg border border-blue-200 bg-white px-3 text-sm font-semibold text-slate-700"
                value={editKind}
                onChange={(event) => setEditKind(event.target.value as SourceKind)}
                aria-label="Edit source kind"
              >
                {sourceKindOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <input
                className="h-10 rounded-lg border border-blue-200 bg-white px-3 text-sm font-semibold text-slate-700"
                type="number"
                min={0}
                max={100}
                value={editProgress}
                onChange={(event) => setEditProgress(Number(event.target.value))}
                aria-label="Edit source progress"
              />
            </div>
            <select
              className="h-10 w-full rounded-lg border border-blue-200 bg-white px-3 text-sm font-semibold text-slate-700"
              value={editStatus}
              onChange={(event) => setEditStatus(event.target.value)}
              aria-label="Edit source status"
            >
              {statusOptionsForKind(editKind).map((option) => (
                <option key={option.slug} value={option.slug}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <button
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
                type="button"
                disabled={!editTitle.trim() || isEditing}
                onClick={handleUpdate}
              >
                <Save size={16} aria-hidden="true" />
                Lưu
              </button>
              <button
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 text-sm font-bold text-red-700 hover:bg-red-50 disabled:bg-slate-100"
                type="button"
                disabled={isEditing}
                onClick={() => selectedNode && archiveConfirm.request(selectedNode.id, () => void handleArchive())}
              >
                <Archive size={16} aria-hidden="true" />
                {selectedNode && archiveConfirm.pendingId === selectedNode.id ? "Bấm lần nữa để lưu trữ" : "Lưu trữ"}
              </button>
            </div>
            <button
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
              type="button"
              onClick={() => void handleToggleLinks()}
            >
              <Link2 size={16} aria-hidden="true" />
              Link tham khảo của nguồn học
            </button>
            {linksOpen ? (
              <EntityLinksPanel
                objectId={selectedNode.id}
                links={linksByObject[selectedNode.id] ?? []}
                error={linkError}
                onLoad={onLoadLinks}
                onCreate={onCreateLink}
                onDelete={onDeleteLink}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto p-3">
        {parents.map(({ node, totalNotes }) => {
          const Icon = iconByKind[node.kind as BuiltInSourceKind] ?? FileText;
          const children = sortedChildren(node.id);
          const expanded = expandedIds.includes(node.id);
          const sourceStats = sourceStatsById.get(node.id);
          const fillPercent = sourceFillPercent(node, sourceStats);

          return (
            <div key={node.id} className="space-y-1">
              <div
                role="button"
                tabIndex={0}
                className={`w-full cursor-pointer rounded-lg border p-3 text-left transition ${
                  selectedId === node.id
                    ? "border-blue-200 bg-blue-50"
                    : "border-transparent bg-white hover:border-line hover:bg-slate-50"
                }`}
                onClick={() => handleNodeSelect(node, children.length > 0)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleNodeSelect(node, children.length > 0);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line bg-white text-slate-600">
                    <Icon size={16} aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      {children.length > 0 ? (
                        expanded ? <ChevronDown size={15} className="shrink-0 text-muted" aria-hidden="true" /> : <ChevronRight size={15} className="shrink-0 text-muted" aria-hidden="true" />
                      ) : null}
                      <p className="min-w-0 flex-1 truncate text-sm font-semibold">{node.title}</p>
                      {renderKindBadge(node, <StatusBadge kind={node.kind} />)}
                    </div>
                    <div
                      className="mt-2 h-1.5 rounded-full bg-slate-100"
                      title={
                        node.progress > 0
                          ? `${node.progress}% progress`
                          : `${sourceStats?.itemWithNotes ?? 0}/${sourceStats?.itemTotal ?? 0} mục có note`
                      }
                    >
                      <div className="h-1.5 rounded-full bg-brand" style={{ width: `${fillPercent}%` }} />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs font-medium text-muted">
                      <span>{totalNotes} note</span>
                      <span>{children.length} mục con</span>
                    </div>
                    {editOpen ? renderRowEditStrip(node) : null}
                  </div>
                </div>
              </div>

              {editOpen && linkEditorId === node.id ? (
                <EntityLinksPanel
                  objectId={node.id}
                  links={linksByObject[node.id] ?? node.links ?? []}
                  error={linkError}
                  onLoad={onLoadLinks}
                  onCreate={onCreateLink}
                  onDelete={onDeleteLink}
                />
              ) : null}

              {expanded && children.length > 0 ? (
                <div className="space-y-1 pl-6">
                  {children.map((child) => renderChildNode(child))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
    {isOpen ? (
      <div className="fixed inset-0 z-40 flex min-h-screen items-center justify-center overflow-y-auto bg-slate-900/40 px-4 py-6">
        <section className="my-auto max-h-[calc(100vh-3rem)] w-full max-w-4xl overflow-y-auto rounded-lg border border-line bg-white shadow-panel">
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-ink">Tạo nguồn mới</h2>
              <p className="mt-1 text-sm font-medium text-muted">Tạo Link, Book, Course, Topic hoặc Output với đủ metadata cần thiết.</p>
            </div>
            <button
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line bg-white text-slate-600 hover:bg-slate-50"
              type="button"
              aria-label="Close create source"
              onClick={closeCreate}
            >
              <X size={17} aria-hidden="true" />
            </button>
          </div>

          {createdSource ? (
            <div className="space-y-4 p-5">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="mt-0.5 text-emerald-700" aria-hidden="true" />
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-emerald-900">Đã tạo nguồn</h3>
                    <p className="mt-1 truncate text-sm font-semibold text-emerald-800">{createdSource.title}</p>
                  </div>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand px-4 text-sm font-bold text-white hover:bg-blue-700"
                  type="button"
                  onClick={closeCreate}
                >
                  <Plus size={16} aria-hidden="true" />
                  Ghi note ngay
                </button>
                {onOpenWorkspace ? (
                  <button
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-line bg-white px-4 text-sm font-bold text-slate-700 hover:bg-slate-50"
                    type="button"
                    onClick={() => {
                      closeCreate();
                      onOpenWorkspace(createdSource);
                    }}
                  >
                    <ArrowRight size={16} aria-hidden="true" />
                    {isWorkspaceLinkKind(createdSource.kind, createdSource.properties) ? "Mở Link workspace" : "Mở Book workspace"}
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="grid gap-0 lg:grid-cols-[260px_minmax(0,1fr)]">
              <aside className="border-b border-line p-3 lg:border-b-0 lg:border-r">
                <div className="space-y-2">
                  {[...createModes].sort((left, right) => {
                    const order: Record<CreateMode, number> = { link: 0, quick: 1, book: 2, course: 3, topic: 4, output: 5 };
                    return order[left.id] - order[right.id];
                  }).map((mode) => (
                    <button
                      key={mode.id}
                      className={`w-full rounded-lg border p-3 text-left transition-colors ${
                        createMode === mode.id ? "border-blue-200 bg-blue-50 text-brand" : "border-line bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                      type="button"
                      onClick={() => handleCreateMode(mode.id)}
                    >
                      <p className="text-sm font-bold">{mode.label}</p>
                      <p className="mt-1 text-xs font-medium text-muted">{mode.description}</p>
                    </button>
                  ))}
                </div>
              </aside>

              <div className="space-y-4 p-5">
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">Tên nguồn</span>
                    <input
                      className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      placeholder={createMode === "link" || createMode === "output" ? "Để trống thì dùng domain làm tên" : "Tên source"}
                      aria-label="Source title"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">Loại</span>
                    <select
                      className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm font-semibold text-slate-700"
                      value={kind}
                      onChange={(event) => setKind(event.target.value as SourceKind)}
                      aria-label="Source kind"
                      disabled={createMode === "output"}
                    >
                      {sourceKindOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {createMode === "link" || createMode === "output" ? (
                  <div className="space-y-3 rounded-lg border border-line bg-slate-50 p-3">
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">URL gốc</span>
                      <input
                        className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
                        value={sourceUrl}
                        onChange={(event) => setSourceUrl(event.target.value)}
                        placeholder="https://substack.com/@..., https://youtu.be/..., https://blog..."
                        aria-label="Source URL"
                      />
                    </label>
                    <div className="grid gap-2 sm:grid-cols-3">
                      <input
                        className="h-10 min-w-0 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
                        value={platform}
                        onChange={(event) => setPlatform(event.target.value)}
                        placeholder="Platform"
                        aria-label="Source platform"
                      />
                      <input
                        className="h-10 min-w-0 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
                        value={author}
                        onChange={(event) => setAuthor(event.target.value)}
                        placeholder="Tác giả/kênh"
                        aria-label="Source author"
                      />
                      <input
                        className="h-10 min-w-0 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink placeholder:text-slate-400"
                        value={linkLabel}
                        onChange={(event) => setLinkLabel(event.target.value)}
                        placeholder="Tên link"
                        aria-label="Link label"
                      />
                    </div>
                    {sourceUrl.trim() && !/^https?:\/\//i.test(sourceUrl.trim()) ? (
                      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                        Link cần bắt đầu bằng http:// hoặc https://.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {createMode !== "quick" ? (
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">Parent</span>
                    <select
                      className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm font-semibold text-slate-700"
                      value={parentId}
                      onChange={(event) => setParentId(event.target.value)}
                      aria-label="Parent source"
                    >
                      <option value="">Không có parent</option>
                      {nodes.map((node) => (
                        <option key={node.id} value={node.id}>
                          {node.title}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                <div className="block">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">Tags</span>
                  <TagPicker tags={createTags} availableTags={availableTags} onChange={setCreateTags} />
                  {parentId ? <p className="mt-1 text-xs text-muted">Kế thừa tag từ parent — có thể thêm/bớt.</p> : null}
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line pt-4">
                  <button
                    className="inline-flex h-10 items-center justify-center rounded-lg border border-line bg-white px-4 text-sm font-bold text-slate-700 hover:bg-slate-50"
                    type="button"
                    onClick={closeCreate}
                  >
                    Hủy
                  </button>
                  <button
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-4 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                    type="button"
                    disabled={(!title.trim() && !sourceUrl.trim()) || Boolean(sourceUrl.trim() && !/^https?:\/\//i.test(sourceUrl.trim())) || isSaving}
                    onClick={handleCreate}
                  >
                    <Plus size={16} aria-hidden="true" />
                    {isSaving ? "Đang lưu" : "Tạo nguồn"}
                  </button>
                </div>
                {sourceError ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                    {sourceError}
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </section>
      </div>
    ) : null}
    </>
  );
}
